import { WSFormProps, WSList, WSText } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import {
  useQueryMemberProfile,
  useQueryUserProfile
} from "../../../../query/users/queries";
import { verificationService } from "../../../../shared/services/verificaiton";
import { CardTaxVerificationRequirementRequester } from "../../components/CardTaxVerificationRequirementRequester";
import { getProfileDataPerson } from "../../components/FormPartialPerson/getProfileDataPerson";
import {
  FormDataPersonalInformation,
  FormPersonalInformation
} from "../../components/FormPersonalInformation";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { useMissingFields } from "../../hooks/useMissingFields";
import { OnboardingModuleCreateAccount, OnboardingStep } from "../../types";
import { CreateAccountContext } from "./types";
import { useRedirectToAccountType } from "./useRedirectToAccountType";
import { useUsPersonMissingFields } from "../../hooks/useUsPersonMissingFields";
import { useQueryUserProfileV2 } from "../../../../query/user/queries/useQueryUserProfile";
import { isSameDateSafe } from "../../../../utils/dates";

export const PersonalInformation: React.FC<OnboardingStep> = ({
  onNext,
  onBack
}) => {
  const [context, setContext] = useOnboardingContext<CreateAccountContext>();

  const userId = useUserId();
  const queryUser = useQueryUserProfile(userId);
  const queryMember = useQueryMemberProfile(userId);
  const queryUserProfile = useQueryUserProfileV2();

  const profileData: WSFormProps<FormDataPersonalInformation>["defaultValues"] =
    useMemo(
      () => ({
        person: getProfileDataPerson(
          "Individual",
          queryUser.data,
          queryMember.data
        ),
        usPerson: {
          firstName: queryUserProfile.data?.taxProfiles?.["US"]?.firstName,
          lastName: queryUserProfile.data?.taxProfiles?.["US"]?.lastName,
          usPersonDate: queryUserProfile.data?.taxProfiles?.["US"]?.startDate,
          usPersonSince: isSameDateSafe(
            queryUser.data?.profile.dob,
            queryUserProfile.data?.taxProfiles?.["US"]?.startDate
          )
            ? "always"
            : "date"
        }
      }),
      [queryMember.data, queryUser.data, queryUserProfile.data]
    );

  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;
  const verificationLevel =
    onboardingModule?.options?.verificationLevel || "Tax";

  const missingFields = useMissingFields(verificationLevel);
  const usPersonMissingFields = useUsPersonMissingFields(verificationLevel);

  const isMismatch = useMemo(
    () => verificationService.getIsIndividualMismatch(missingFields),
    [missingFields]
  );

  const isUsPersonMismatch = useMemo(
    () => verificationService.getIsUsPersonMismatch(usPersonMissingFields),
    [usPersonMissingFields]
  );

  useRedirectToAccountType();

  return (
    <WSList gap="2XL">
      <CardTaxVerificationRequirementRequester />

      <FormPersonalInformation
        isUsPerson={context.accountType?.isUsPerson}
        defaultValues={context.personalInformation || profileData}
        onBack={onBack}
        onSubmit={data => {
          setContext(prev => ({ ...prev, personalInformation: data }));
          onNext?.();
        }}
        mismatch={isMismatch}
        usPersonMismatch={isUsPersonMismatch}
        country={context.accountType?.country || "US"}
      />

      <WSText.ParagraphXs color="gray400">
        The information on this page is required to setup your Wingspan account,
        if you exit or leave without completing this step, you will return to
        this page the next time you sign-in
      </WSText.ParagraphXs>
    </WSList>
  );
};
